import { z } from "zod";

export const ConsentStateSchema = z.object({
  analytics: z.boolean().optional(),
  marketing: z.boolean().optional(),
  adStorage: z.enum(["granted", "denied"]).optional(),
  analyticsStorage: z.enum(["granted", "denied"]).optional(),
  adUserData: z.enum(["granted", "denied"]).optional(),
  adPersonalization: z.enum(["granted", "denied"]).optional(),
});

export type ConsentState = z.infer<typeof ConsentStateSchema>;

export const AnalyticsEventNameSchema = z
  .string()
  .trim()
  .min(1)
  .regex(/^[a-z0-9_]+$/i, "Event names should use snake_case or GA4-style names.");

export type AnalyticsEventName = string;

export type AnalyticsPayload = Record<string, unknown>;

export type QueuePolicy = "emit" | "drop" | "queue";

export interface AnalyticsContext {
  app: string;
  env: string;
  locale?: string;
  currency?: string;
  consent?: ConsentState;
}

export interface TrackOptions {
  eventId?: string;
  queuePolicy?: QueuePolicy;
  consentOverride?: ConsentState;
  destinationHint?: "ga4" | "google_ads" | "all";
}

export interface NormalizedTrackEvent {
  event: string;
  event_name: string;
  event_id: string;
  event_ts: number;
  app: string;
  env: string;
  page_path?: string;
  page_location?: string;
  page_title?: string;
  locale?: string;
  currency?: string;
  consent_state?: ConsentState;
  destination_hint?: "ga4" | "google_ads" | "all";
  [key: string]: unknown;
}

export interface AnalyticsClient {
  track: (
    eventName: AnalyticsEventName,
    payload?: AnalyticsPayload,
    options?: TrackOptions,
  ) => void;
  flushQueue: () => void;
  getQueueSize: () => number;
  updateConsent: (consent: ConsentState) => void;
}

export interface CreateAnalyticsClientArgs {
  app: string;
  env: string;
  getLocale?: () => string | undefined;
  getCurrency?: () => string | undefined;
  getConsent?: () => ConsentState | undefined;
  queueLimit?: number;
}

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

const canonicalGa4Events = new Set([
  "page_view",
  "view_item",
  "view_item_list",
  "select_item",
  "add_to_cart",
  "remove_from_cart",
  "view_cart",
  "begin_checkout",
  "add_shipping_info",
  "add_payment_info",
  "purchase",
  "sign_up",
  "login",
  "search",
  "generate_lead",
  "contact",
]);

const customApprovedEvents = new Set([
  "custom_event",
  "user_identified",
  "user_properties_updated",
  "user_reset",
]);

function isWindowAvailable() {
  return typeof window !== "undefined";
}

function createEventId(): string {
  const nowPart = Date.now().toString(36);

  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${nowPart}-${crypto.randomUUID()}`;
  }

  return `${nowPart}-${Math.random().toString(36).slice(2, 11)}`;
}

function inferPageContext() {
  if (!isWindowAvailable()) return {};

  return {
    page_path: window.location?.pathname,
    page_location: window.location?.href,
    page_title: globalThis.document?.title,
  };
}

function shouldEmitByConsent(consent: ConsentState | undefined): boolean {
  if (!consent) return true;

  if (consent.analytics === false && consent.marketing === false) {
    return false;
  }

  if (consent.analyticsStorage === "denied" && consent.adStorage === "denied") {
    return false;
  }

  return true;
}

export function normalizeTrackEvent(args: {
  eventName: AnalyticsEventName;
  payload?: AnalyticsPayload;
  context: AnalyticsContext;
  options?: TrackOptions;
}): NormalizedTrackEvent {
  const parsedName = AnalyticsEventNameSchema.parse(args.eventName);
  const eventId = args.options?.eventId?.trim() || createEventId();
  const now = Date.now();
  const consentState = ConsentStateSchema.optional().parse(
    args.options?.consentOverride ?? args.context.consent,
  );

  const normalized: NormalizedTrackEvent = {
    event: parsedName,
    event_name: parsedName,
    event_id: eventId,
    event_ts: now,
    app: args.context.app,
    env: args.context.env,
    ...inferPageContext(),
    ...(args.context.locale ? { locale: args.context.locale } : {}),
    ...(args.context.currency ? { currency: args.context.currency } : {}),
    ...(consentState ? { consent_state: consentState } : {}),
    ...(args.options?.destinationHint ? { destination_hint: args.options.destinationHint } : {}),
    ...(args.payload ?? {}),
  };

  return normalized;
}

export function isCanonicalEvent(eventName: string): boolean {
  return canonicalGa4Events.has(eventName) || customApprovedEvents.has(eventName);
}

export function pushToDataLayer(event: Record<string, unknown>): void {
  if (!isWindowAvailable()) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

export function createAnalyticsClient(args: CreateAnalyticsClientArgs): AnalyticsClient {
  const queue: NormalizedTrackEvent[] = [];
  let latestConsent = args.getConsent?.();
  const queueLimit = Math.max(args.queueLimit ?? 100, 10);

  function context(): AnalyticsContext {
    return {
      app: args.app,
      env: args.env,
      locale: args.getLocale?.(),
      currency: args.getCurrency?.(),
      consent: latestConsent,
    };
  }

  function flushQueue() {
    if (!shouldEmitByConsent(latestConsent)) return;

    while (queue.length > 0) {
      const item = queue.shift();
      if (item) {
        pushToDataLayer(item);
      }
    }
  }

  return {
    track(eventName, payload, options) {
      const normalized = normalizeTrackEvent({
        eventName,
        payload,
        context: context(),
        options,
      });

      const queuePolicy = options?.queuePolicy ?? "emit";
      const allowEmit = shouldEmitByConsent(options?.consentOverride ?? latestConsent);

      if (!isCanonicalEvent(normalized.event_name)) {
        normalized.event = "custom_event";
        normalized.event_name = eventName;
      }

      if (allowEmit) {
        pushToDataLayer(normalized);
        return;
      }

      if (queuePolicy === "drop") {
        return;
      }

      if (queuePolicy === "queue") {
        if (queue.length >= queueLimit) {
          queue.shift();
        }
        queue.push(normalized);
      }
    },
    flushQueue,
    getQueueSize() {
      return queue.length;
    },
    updateConsent(consent) {
      latestConsent = ConsentStateSchema.parse(consent);
      flushQueue();
    },
  };
}

export const CanonicalEvents = {
  PAGE_VIEW: "page_view",
  VIEW_ITEM: "view_item",
  VIEW_ITEM_LIST: "view_item_list",
  SELECT_ITEM: "select_item",
  ADD_TO_CART: "add_to_cart",
  REMOVE_FROM_CART: "remove_from_cart",
  VIEW_CART: "view_cart",
  BEGIN_CHECKOUT: "begin_checkout",
  ADD_SHIPPING_INFO: "add_shipping_info",
  ADD_PAYMENT_INFO: "add_payment_info",
  PURCHASE: "purchase",
  SIGN_UP: "sign_up",
  LOGIN: "login",
  SEARCH: "search",
  GENERATE_LEAD: "generate_lead",
  CONTACT: "contact",
  CUSTOM_EVENT: "custom_event",
} as const;
